import React from 'react'
import { NavLink, useLocation } from 'react-router-dom'

import { ChevronDown, type LucideIcon } from 'lucide-react'

import { useAuth } from '@/hooks/useAuth'
import { cn } from '@/utils/cn'

import { useSidebar } from './useSidebar'

export interface SidebarItemProps {
  id?: string
  label: string
  href?: string
  icon?: LucideIcon
  roles?: string[]
  children?: SidebarItemProps[]
  badge?: string | number
  badgeVariant?: 'default' | 'primary' | 'warning' | 'danger'
  onClick?: () => void
  external?: boolean
}

const badgeStyles = {
  default: 'bg-gray-200 text-gray-700',
  primary: 'bg-blue-100 text-blue-700',
  warning: 'bg-yellow-100 text-yellow-700',
  danger: 'bg-red-100 text-red-700'
}

export const SidebarItem: React.FC<SidebarItemProps & { level?: number }> = ({
  id,
  label,
  href,
  icon: Icon,
  roles,
  children,
  badge,
  badgeVariant = 'default',
  onClick,
  external,
  level = 0
}) => {
  const location = useLocation()
  const { isCollapsed, openItems, toggleItem } = useSidebar()
  const { hasRoles } = useAuth()

  // Generar ID único si no se proporciona
  const itemId = id || `${label}-${href || 'group'}`
  const isOpen = openItems.includes(itemId)
  const hasChildren = children && children.length > 0

  // Verificar permisos
  if (roles && !hasRoles(roles)) {
    return null
  }

  // Verificar si algún hijo está activo
  const isChildActive =
    hasChildren &&
    children.some((child) => {
      if (child.href === location.pathname) return true
      if (child.children) {
        return child.children.some((subChild) => subChild.href === location.pathname)
      }
      return false
    })

  const isActive = href === location.pathname || isChildActive

  const handleClick = (e: React.MouseEvent) => {
    if (hasChildren) {
      e.preventDefault()
      toggleItem(itemId)
    }
    onClick?.()
  }

  const itemContent = (
    <>
      {Icon && (
        <Icon
          className={cn(
            'flex-shrink-0 transition-all duration-200',
            isCollapsed ? 'h-5 w-5' : 'h-5 w-5',
            isActive ? 'text-blue-600' : 'text-gray-400 group-hover:text-gray-600'
          )}
        />
      )}

      {!isCollapsed && (
        <>
          <span
            className={cn(
              'flex-1 text-sm font-medium transition-colors duration-200',
              isActive ? 'text-gray-900' : 'text-gray-700 group-hover:text-gray-900'
            )}
          >
            {label}
          </span>

          {badge !== undefined && (
            <span
              className={cn(
                'ml-auto inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium',
                badgeStyles[badgeVariant]
              )}
            >
              {badge}
            </span>
          )}

          {hasChildren && (
            <ChevronDown
              className={cn(
                'ml-auto h-4 w-4 transition-transform duration-200',
                isOpen ? 'transform rotate-180' : '',
                'text-gray-400'
              )}
            />
          )}

          {external && (
            <svg
              className="ml-2 h-4 w-4 text-gray-400"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
              />
            </svg>
          )}
        </>
      )}
    </>
  )

  const itemClasses = cn(
    'group flex items-center gap-3 px-3 py-2 rounded-lg transition-all duration-200',
    'hover:bg-gray-100',
    isActive && 'bg-blue-50 hover:bg-blue-100',
    isCollapsed && 'justify-center',
    level > 0 && !isCollapsed && 'ml-6'
  )

  return (
    <>
      {href && !hasChildren ? (
        external ? (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className={itemClasses}
            onClick={onClick}
          >
            {itemContent}
          </a>
        ) : (
          <NavLink to={href} className={itemClasses} onClick={onClick}>
            {itemContent}
          </NavLink>
        )
      ) : (
        <button className={itemClasses} onClick={handleClick}>
          {/* <button className={cn(itemClasses, 'w-full')} onClick={handleClick}> */}
          {itemContent}
        </button>
      )}

      {/* Submenú */}
      {hasChildren && !isCollapsed && (
        <div
          className={cn(
            'overflow-hidden transition-all duration-200',
            isOpen ? 'max-h-screen' : 'max-h-0'
          )}
        >
          <div className="mt-1 space-y-1">
            {children.map((child, index) => (
              <SidebarItem
                key={child.id || `${child.label}-${index}`}
                {...child}
                level={level + 1}
              />
            ))}
          </div>
        </div>
      )}

      {/* Tooltip para items colapsados */}
      {isCollapsed && !hasChildren && (
        <div className="absolute left-full ml-2 px-2 py-1 bg-gray-900 text-white text-sm rounded-md opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 whitespace-nowrap z-50">
          {label}
          <div className="absolute right-full top-1/2 -translate-y-1/2 border-4 border-transparent border-r-gray-900" />
        </div>
      )}
    </>
  )
}
